import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ArrowRightLeft } from "lucide-react";

// One-click workload rebalance: hand a job's assignment from one recruiter to another
// (role + targets travel with the assignment).
export default function TransferJobDialog({ transfer, recruiters, onClose, onSaved }) {
    const [toId, setToId] = useState("");
    const [saving, setSaving] = useState(false);
    if (!transfer) return null;
    const { job, fromId, fromName } = transfer;
    const currentIds = new Set((job.assignments || []).map((a) => a.user_id));
    const options = (recruiters || []).filter((r) => r.id !== fromId && !currentIds.has(r.id));

    const save = async () => {
        if (!toId) return;
        setSaving(true);
        try {
            const assignments = (job.assignments || []).map((a) => ({
                user_id: a.user_id === fromId ? toId : a.user_id,
                role: a.role,
                targets: a.targets || {},
            }));
            await api.patch(`/jobs/${job.id}/assignments`, { assignments });
            toast.success("Job transferred");
            onSaved?.();
            onClose?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent className="max-w-sm" data-testid="transfer-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2">
                        <ArrowRightLeft className="w-4 h-4 text-ikb" /> Transfer job
                    </DialogTitle>
                </DialogHeader>
                <p className="text-sm text-foreground/80">
                    Move <span className="font-medium">{job.title}</span> from <span className="font-medium">{fromName}</span> to:
                </p>
                <select
                    value={toId}
                    onChange={(e) => setToId(e.target.value)}
                    className="w-full h-9 border border-border px-2 text-sm bg-white outline-none focus:border-ikb"
                    data-testid="transfer-target-select"
                >
                    <option value="">Choose recruiter…</option>
                    {options.map((r) => <option key={r.id} value={r.id}>{r.name || r.email}</option>)}
                </select>
                <p className="text-[11px] text-foreground/60">Role and targets carry over to the new recruiter.</p>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={save} disabled={!toId || saving} className="bg-ikb hover:bg-[#001f7a]" data-testid="transfer-dialog-save">
                        {saving ? "Transferring…" : "Transfer"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
